import { useParams, useSearch } from "@tanstack/react-router";
import { Ban, Pencil, PowerOff, ShieldAlert, ShieldCheck, ShieldX, SlidersHorizontal, XCircle } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import { useAdminAllowStationMutation, useAdminBanStationMutation, useClearAdminAllowStationMutation, useClearAdminBanStationMutation, useDecommissionStationMutation, useIssueStationOverrideMutation, useRegistrationRequestQuery, useStationAuditQuery, useStationCertsQuery, useStationLicenseStateQuery, useStationOverridesQuery, useStationQuery, useUpdateStationPolicyMutation } from "@/api/query-hooks";
import type { StationAdminControlRecord, StationLicenseStateRecord } from "@/api/types";
import { queryClient } from "@/app/query-client";
import { isAdmin } from "@/auth/capabilities";
import { useAuth } from "@/auth/use-auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, ErrorState, LoadingState } from "@/components/layout/route-states";
import { ClientIdentity } from "@/features/licensing/client-identity";
import { formatDateTime } from "@/lib/date";
import { formatRedactedJson } from "@/lib/redact";

export function StationDetailPage() {
	const auth = useAuth();
	return isAdmin(auth.roles) ? <AdminStationDetailPage /> : <RecoveryStationDetailPage />;
}

function AdminStationDetailPage() {
  const { stationId } = useParams({ strict: false }) as { stationId: string };
  const { t, i18n } = useTranslation(["stations", "audit", "common", "registrationRequests"]);
  const station = useStationQuery(stationId);
  const licenseState = useStationLicenseStateQuery(stationId);
  const certs = useStationCertsQuery(stationId);
  const overrides = useStationOverridesQuery(stationId);
  const audit = useStationAuditQuery(stationId);
  const issueOverride = useIssueStationOverrideMutation();
  const decommissionStation = useDecommissionStationMutation();
  const adminBanStation = useAdminBanStationMutation();
  const adminAllowStation = useAdminAllowStationMutation();
  const clearAdminBanStation = useClearAdminBanStationMutation();
  const clearAdminAllowStation = useClearAdminAllowStationMutation();
  const updateStationPolicy = useUpdateStationPolicyMutation();
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [decommissionOpen, setDecommissionOpen] = useState(false);
  const [decommissionReason, setDecommissionReason] = useState("");
  const [banOpen, setBanOpen] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [banValidUntil, setBanValidUntil] = useState("");
  const [clearBanOpen, setClearBanOpen] = useState(false);
  const [clearBanReason, setClearBanReason] = useState("");
  const [allowOpen, setAllowOpen] = useState(false);
  const [allowReason, setAllowReason] = useState("");
  const [allowValidUntil, setAllowValidUntil] = useState(() => defaultDateTimeLocal(24));
  const [clearAllowOpen, setClearAllowOpen] = useState(false);
  const [clearAllowReason, setClearAllowReason] = useState("");
  const [policyOpen, setPolicyOpen] = useState(false);
  const [policyReason, setPolicyReason] = useState("");
  const [policyNotes, setPolicyNotes] = useState("");
  const [policyReleaseChannel, setPolicyReleaseChannel] = useState("DEFAULT");
  const [policyThresholdDays, setPolicyThresholdDays] = useState("");
  const [policyWarningDays, setPolicyWarningDays] = useState("");
  const [policyOfflineGraceDays, setPolicyOfflineGraceDays] = useState("");
  const [policyReadonlyGraceDays, setPolicyReadonlyGraceDays] = useState("");

  if (station.isLoading) {
    return <LoadingState />;
  }
  if (station.isError) {
    return <ErrorState title={t("common:states.error")} detail={station.error.message} onRetry={() => void station.refetch()} />;
  }

  const value = station.data;
  const isDecommissioned = value?.status === "DECOMMISSIONED" || Boolean(value?.decommissionedAt);
  const setBanDialogOpen = (open: boolean) => {
    setBanOpen(open);
    if (open && value?.adminBan?.active) {
      setBanReason(value.adminBan.reason ?? "");
      setBanValidUntil(dateTimeISOToLocal(value.adminBan.validUntil));
    }
    if (open && !value?.adminBan?.active) {
      setBanReason("");
      setBanValidUntil("");
    }
  };
  const setAllowDialogOpen = (open: boolean) => {
    setAllowOpen(open);
    if (open && value?.adminAllow?.active) {
      setAllowReason(value.adminAllow.reason ?? "");
      setAllowValidUntil(dateTimeISOToLocal(value.adminAllow.validUntil));
    }
    if (open && !value?.adminAllow?.active) {
      setAllowReason("");
      setAllowValidUntil(defaultDateTimeLocal(24));
    }
  };
  const setPolicyDialogOpen = (open: boolean) => {
    setPolicyOpen(open);
    if (open && value) {
      setPolicyReason("");
      setPolicyNotes(value.policy?.notes ?? "");
      setPolicyReleaseChannel(value.policy?.releaseChannelOverride || "DEFAULT");
      setPolicyThresholdDays(value.policy?.thresholdDaysOverride ? String(value.policy.thresholdDaysOverride) : "");
      setPolicyWarningDays(value.policy?.warningDaysOverride ? String(value.policy.warningDaysOverride) : "");
      setPolicyOfflineGraceDays(value.policy?.offlineGraceDaysOverride ? String(value.policy.offlineGraceDaysOverride) : "");
      setPolicyReadonlyGraceDays(value.policy?.readonlyGraceDaysOverride ? String(value.policy.readonlyGraceDaysOverride) : "");
    }
  };
  const submitOverride = () => {
    const reason = overrideReason.trim();
    if (!reason || !value) {
      return;
    }
    issueOverride.mutate(
      { stationId: value.id, reason },
      {
        onSuccess: () => {
          setOverrideReason("");
          setOverrideOpen(false);
          toast.success(t("overrideIssued"));
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : t("common:states.error"));
        }
      }
    );
  };
  const submitDecommission = () => {
    const reason = decommissionReason.trim();
    if (!reason || !value) {
      return;
    }
    decommissionStation.mutate(
      { stationId: value.id, reason },
      {
        onSuccess: () => {
          setDecommissionReason("");
          setDecommissionOpen(false);
          toast.success(t("decommissioned"));
        },
        onError: (error) => {
		  if (error instanceof ApiError && error.code === "authentication_retry_required") {
			setDecommissionReason("");
			setDecommissionOpen(false);
			void queryClient.invalidateQueries({ queryKey: ["station", value.id] });
			toast.error(t("registrationRequests:authRetryRequired"));
			return;
		  }
          toast.error(error instanceof Error ? error.message : t("common:states.error"));
        }
      }
    );
  };
  const submitBan = () => {
    const reason = banReason.trim();
    if (!reason || !value) {
      return;
    }
    adminBanStation.mutate(
      { stationId: value.id, reason, validUntil: dateTimeLocalToISO(banValidUntil) },
      {
        onSuccess: () => {
          setBanReason("");
          setBanValidUntil("");
          setBanOpen(false);
          toast.success(t("adminBanned"));
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : t("common:states.error"));
        }
      }
    );
  };
  const submitAllow = () => {
    const reason = allowReason.trim();
    if (!reason || !value) {
      return;
    }
    adminAllowStation.mutate(
      { stationId: value.id, reason, validUntil: dateTimeLocalToISO(allowValidUntil) },
      {
        onSuccess: () => {
          setAllowReason("");
          setAllowValidUntil(defaultDateTimeLocal(24));
          setAllowOpen(false);
          toast.success(t("adminAllowed"));
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : t("common:states.error"));
        }
      }
    );
  };
  const submitClearBan = () => {
    const reason = clearBanReason.trim();
    if (!reason || !value) {
      return;
    }
    clearAdminBanStation.mutate(
      { stationId: value.id, reason },
      {
        onSuccess: () => {
          setClearBanReason("");
          setClearBanOpen(false);
          toast.success(t("adminBanCleared"));
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : t("common:states.error"));
        }
      }
    );
  };
  const submitClearAllow = () => {
    const reason = clearAllowReason.trim();
    if (!reason || !value) {
      return;
    }
    clearAdminAllowStation.mutate(
      { stationId: value.id, reason },
      {
        onSuccess: () => {
          setClearAllowReason("");
          setClearAllowOpen(false);
          toast.success(t("adminAllowCleared"));
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : t("common:states.error"));
        }
      }
    );
  };
  const submitPolicy = () => {
    const reason = policyReason.trim();
    if (!reason || !value) {
      return;
    }
    const thresholdDaysOverride = parseOptionalPositiveInt(policyThresholdDays);
    const warningDaysOverride = parseOptionalPositiveInt(policyWarningDays);
    const offlineGraceDaysOverride = parseOptionalPositiveInt(policyOfflineGraceDays);
    const readonlyGraceDaysOverride = parseOptionalPositiveInt(policyReadonlyGraceDays);
    if ([thresholdDaysOverride, warningDaysOverride, offlineGraceDaysOverride, readonlyGraceDaysOverride].includes(null)) {
      toast.error(t("policyInvalidNumber"));
      return;
    }
    updateStationPolicy.mutate(
      {
        stationId: value.id,
        reason,
        notes: policyNotes,
        releaseChannelOverride: policyReleaseChannel === "DEFAULT" ? "" : policyReleaseChannel,
        thresholdDaysOverride: thresholdDaysOverride ?? undefined,
        warningDaysOverride: warningDaysOverride ?? undefined,
        offlineGraceDaysOverride: offlineGraceDaysOverride ?? undefined,
        readonlyGraceDaysOverride: readonlyGraceDaysOverride ?? undefined
      },
      {
        onSuccess: () => {
          setPolicyReason("");
          setPolicyDialogOpen(false);
          toast.success(t("policyUpdated"));
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : t("common:states.error"));
        }
      }
    );
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold">{t("detailTitle")} {value?.id}</h2>
          <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">{value?.appId} / <ClientIdentity clientId={value?.clientId} compact /></p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={value?.status === "HEALTHY" ? "success" : value?.status === "DECOMMISSIONED" ? "default" : "warning"}>{value?.status}</Badge>
          <Dialog open={policyOpen} onOpenChange={setPolicyDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" disabled={isDecommissioned}>
                <SlidersHorizontal className="h-4 w-4" />
                {t("editPolicy")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("policyDialogTitle")}</DialogTitle>
                <DialogDescription>{t("policyDialogDescription")}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="policy-channel">{t("policyChannel")}</Label>
                  <Select value={policyReleaseChannel} onValueChange={setPolicyReleaseChannel}>
                    <SelectTrigger id="policy-channel" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DEFAULT">{t("policyDefault")}</SelectItem>
                      <SelectItem value="STABLE">STABLE</SelectItem>
                      <SelectItem value="BETA">BETA</SelectItem>
                      <SelectItem value="ALPHA">ALPHA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <PolicyNumberInput id="policy-threshold" label={t("policyThreshold")} value={policyThresholdDays} onChange={setPolicyThresholdDays} />
                  <PolicyNumberInput id="policy-warning" label={t("policyWarning")} value={policyWarningDays} onChange={setPolicyWarningDays} />
                  <PolicyNumberInput id="policy-offline" label={t("policyOfflineGrace")} value={policyOfflineGraceDays} onChange={setPolicyOfflineGraceDays} />
                  <PolicyNumberInput id="policy-readonly" label={t("policyReadonlyGrace")} value={policyReadonlyGraceDays} onChange={setPolicyReadonlyGraceDays} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="policy-notes">{t("notes")}</Label>
                  <Textarea id="policy-notes" value={policyNotes} maxLength={2000} onChange={(event) => setPolicyNotes(event.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="policy-reason">{t("reason")}</Label>
                  <Textarea id="policy-reason" value={policyReason} maxLength={500} onChange={(event) => setPolicyReason(event.target.value)} />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setPolicyDialogOpen(false)}>
                  {t("common:actions.cancel")}
                </Button>
                <Button type="button" onClick={submitPolicy} disabled={updateStationPolicy.isPending || policyReason.trim().length === 0}>
                  <SlidersHorizontal className="h-4 w-4" />
                  {t("policySubmit")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={allowOpen} onOpenChange={setAllowDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" disabled={isDecommissioned}>
                {value?.adminAllow?.active ? <Pencil className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                {value?.adminAllow?.active ? t("editAdminAllow") : t("adminAllow")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("adminAllowDialogTitle")}</DialogTitle>
                <DialogDescription>{t("adminAllowDialogDescription")}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="allow-valid-until">{t("validUntil")}</Label>
                  <Input
                    id="allow-valid-until"
                    type="datetime-local"
                    value={allowValidUntil}
                    onChange={(event) => setAllowValidUntil(event.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="allow-reason">{t("reason")}</Label>
                  <Textarea id="allow-reason" value={allowReason} maxLength={500} onChange={(event) => setAllowReason(event.target.value)} />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setAllowOpen(false)}>
                  {t("common:actions.cancel")}
                </Button>
                <Button type="button" onClick={submitAllow} disabled={adminAllowStation.isPending || allowReason.trim().length === 0}>
                  <ShieldCheck className="h-4 w-4" />
                  {t("adminAllowSubmit")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={banOpen} onOpenChange={setBanDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" disabled={isDecommissioned}>
                {value?.adminBan?.active ? <Pencil className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                {value?.adminBan?.active ? t("editAdminBan") : t("adminBan")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("adminBanDialogTitle")}</DialogTitle>
                <DialogDescription>{t("adminBanDialogDescription")}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="ban-valid-until">{t("validUntil")}</Label>
                  <Input
                    id="ban-valid-until"
                    type="datetime-local"
                    value={banValidUntil}
                    onChange={(event) => setBanValidUntil(event.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ban-reason">{t("reason")}</Label>
                  <Textarea id="ban-reason" value={banReason} maxLength={500} onChange={(event) => setBanReason(event.target.value)} />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setBanOpen(false)}>
                  {t("common:actions.cancel")}
                </Button>
                <Button type="button" variant="destructive" onClick={submitBan} disabled={adminBanStation.isPending || banReason.trim().length === 0}>
                  <Ban className="h-4 w-4" />
                  {t("adminBanSubmit")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={decommissionOpen} onOpenChange={setDecommissionOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" disabled={isDecommissioned}>
                <PowerOff className="h-4 w-4" />
                {t("decommission")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("decommissionDialogTitle")}</DialogTitle>
                <DialogDescription>{t("decommissionDialogDescription")}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-2">
                <Label htmlFor="decommission-reason">{t("reason")}</Label>
                <Textarea
                  id="decommission-reason"
                  value={decommissionReason}
                  maxLength={500}
                  onChange={(event) => setDecommissionReason(event.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDecommissionOpen(false)}>
                  {t("common:actions.cancel")}
                </Button>
                <Button type="button" variant="destructive" onClick={submitDecommission} disabled={decommissionStation.isPending || decommissionReason.trim().length === 0}>
                  <PowerOff className="h-4 w-4" />
                  {t("decommissionSubmit")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <ClearAdminControlDialog
            open={clearAllowOpen}
            title={t("clearAdminAllowDialogTitle")}
            description={t("clearAdminAllowDialogDescription")}
            reason={clearAllowReason}
            pending={clearAdminAllowStation.isPending}
            onOpenChange={setClearAllowOpen}
            onReasonChange={setClearAllowReason}
            onSubmit={submitClearAllow}
          />
          <ClearAdminControlDialog
            open={clearBanOpen}
            title={t("clearAdminBanDialogTitle")}
            description={t("clearAdminBanDialogDescription")}
            reason={clearBanReason}
            pending={clearAdminBanStation.isPending}
            destructive
            onOpenChange={setClearBanOpen}
            onReasonChange={setClearBanReason}
            onSubmit={submitClearBan}
          />
        </div>
      </div>

      <section className="grid gap-3 rounded-md border bg-panel p-4 md:grid-cols-2 xl:grid-cols-4">
        <Meta label={t("hostname")} value={value?.hostname || "—"} />
        <Meta label={t("version")} value={value?.currentVersion || "—"} />
        <Meta label={t("platform")} value={`${value?.os ?? "—"}/${value?.arch ?? "—"}`} />
        <Meta label={t("lastSeen")} value={formatDateTime(value?.lastSeenAt, i18n.language)} />
        <Meta label={t("channel")} value={value?.releaseChannel || "—"} />
        <Meta label={t("enrolled")} value={formatDateTime(value?.enrolledAt, i18n.language)} />
        <Meta label={t("decommissionedAt")} value={formatDateTime(value?.decommissionedAt, i18n.language)} />
        <Meta label="IP" value={value?.lastSeenIp || "—"} />
        <Meta label={t("adminBan")} value={formatAdminControl(value?.adminBan, i18n.language, t)} />
        <Meta label={t("adminAllow")} value={formatAdminControl(value?.adminAllow, i18n.language, t)} />
        <Meta label={t("policyChannel")} value={value?.policy?.releaseChannelOverride || "—"} />
        <Meta label={t("policyThreshold")} value={formatPolicyNumber(value?.policy?.thresholdDaysOverride)} />
        <Meta label={t("policyWarning")} value={formatPolicyNumber(value?.policy?.warningDaysOverride)} />
        <Meta label={t("policyOfflineGrace")} value={formatPolicyNumber(value?.policy?.offlineGraceDaysOverride)} />
        <Meta label={t("policyReadonlyGrace")} value={formatPolicyNumber(value?.policy?.readonlyGraceDaysOverride)} />
        <Meta label={t("notes")} value={value?.policy?.notes || "—"} />
        <Meta label={t("installationId")} value={value?.installationId || "—"} mono />
        <Meta label={t("fingerprint")} value={value?.fingerprintHash || "—"} mono />
      </section>

      <section className="grid gap-3 md:grid-cols-2">
        <AdminControlCard
          title={t("adminBan")}
          control={value?.adminBan}
          language={i18n.language}
          t={t}
          destructive
          disabled={isDecommissioned}
          onEdit={() => setBanDialogOpen(true)}
          onClear={() => {
            setClearBanReason("");
            setClearBanOpen(true);
          }}
        />
        <AdminControlCard
          title={t("adminAllow")}
          control={value?.adminAllow}
          language={i18n.language}
          t={t}
          disabled={isDecommissioned}
          onEdit={() => setAllowDialogOpen(true)}
          onClear={() => {
            setClearAllowReason("");
            setClearAllowOpen(true);
          }}
        />
      </section>

      <section className="rounded-md border bg-panel">
        <Header title={t("licenseState")} />
        {licenseState.isError ? (
          <div className="p-4"><ErrorState title={t("common:states.error")} detail={licenseState.error.message} onRetry={() => void licenseState.refetch()} /></div>
        ) : (
          <LicenseStateBlock state={licenseState.data} language={i18n.language} t={t} />
        )}
      </section>

      <section className="rounded-md border bg-panel">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
          <div className="text-sm font-medium">{t("overrides")}</div>
          <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline">
                <ShieldAlert className="h-4 w-4" />
                {t("issueOverride")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("overrideDialogTitle")}</DialogTitle>
                <DialogDescription>{t("overrideDialogDescription")}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-2">
                <Label htmlFor="override-reason">{t("reason")}</Label>
                <Textarea
                  id="override-reason"
                  value={overrideReason}
                  maxLength={500}
                  onChange={(event) => setOverrideReason(event.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOverrideOpen(false)}>
                  {t("common:actions.cancel")}
                </Button>
                <Button type="button" onClick={submitOverride} disabled={issueOverride.isPending || overrideReason.trim().length === 0}>
                  <ShieldAlert className="h-4 w-4" />
                  {t("overrideSubmit")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        {overrides.isError ? (
          <div className="p-4"><ErrorState title={t("common:states.error")} detail={overrides.error.message} onRetry={() => void overrides.refetch()} /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">ID</th>
                  <th className="px-3 py-2">{t("issuedAt")}</th>
                  <th className="px-3 py-2">{t("validUntil")}</th>
                  <th className="px-3 py-2">{t("issuedBy")}</th>
                  <th className="px-3 py-2">{t("reason")}</th>
                  <th className="px-3 py-2">{t("status")}</th>
                </tr>
              </thead>
              <tbody>
                {(overrides.data?.items ?? []).length === 0 ? (
                  <tr>
                    <td className="px-3 py-3 text-muted-foreground" colSpan={6}>{t("noOverrides")}</td>
                  </tr>
                ) : (
                  (overrides.data?.items ?? []).map((override) => (
                    <tr key={override.id} className="border-t">
                      <td className="max-w-48 truncate px-3 py-2 font-mono text-xs">{override.id}</td>
                      <td className="px-3 py-2">{formatDateTime(override.issuedAt, i18n.language)}</td>
                      <td className="px-3 py-2">{formatDateTime(override.validUntil, i18n.language)}</td>
                      <td className="px-3 py-2">{override.issuedByEmployeeId}</td>
                      <td className="max-w-80 truncate px-3 py-2">{override.reason}</td>
                      <td className="px-3 py-2"><Badge variant={override.status === "ACTIVE" ? "success" : "default"}>{override.status}</Badge></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-md border bg-panel">
        <Header title={t("certs")} />
        {certs.isError ? (
          <div className="p-4"><ErrorState title={t("common:states.error")} detail={certs.error.message} onRetry={() => void certs.refetch()} /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">ID</th>
                  <th className="px-3 py-2">Issued</th>
                  <th className="px-3 py-2">Not after</th>
                  <th className="px-3 py-2">Key</th>
                </tr>
              </thead>
              <tbody>
                {(certs.data?.items ?? []).map((cert) => (
                  <tr key={cert.id} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{cert.id}</td>
                    <td className="px-3 py-2">{formatDateTime(cert.issuedAt, i18n.language)}</td>
                    <td className="px-3 py-2">{formatDateTime(cert.notAfter, i18n.language)}</td>
                    <td className="px-3 py-2">{cert.signingKeyVersion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-md border bg-panel">
        <Header title={t("audit")} />
        {audit.isLoading ? (
          <div className="p-4"><LoadingState /></div>
        ) : audit.isError ? (
          <div className="p-4"><ErrorState title={t("common:states.error")} detail={audit.error.message} onRetry={() => void audit.refetch()} /></div>
        ) : (audit.data?.items ?? []).length === 0 ? (
          <div className="p-4"><EmptyState title={t("common:states.empty")} /></div>
        ) : (
          <div className="divide-y">
            {(audit.data?.items ?? []).map((entry) => (
              <details key={entry.id} className="group p-3">
                <summary className="grid cursor-pointer list-none gap-1 text-sm md:grid-cols-[180px_1fr_180px]">
                  <span className="text-muted-foreground">{formatDateTime(entry.at, i18n.language)}</span>
                  <span className="font-medium">{entry.event}</span>
                  <span className="truncate text-muted-foreground">{entry.initiatedByEmployeeId || "—"}</span>
                </summary>
                <pre className="mt-2 overflow-x-auto rounded bg-muted p-3 text-xs">{formatRedactedJson(entry.payload)}</pre>
              </details>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function RecoveryStationDetailPage() {
  const { stationId } = useParams({ strict: false }) as { stationId: string };
  const search = useSearch({ from: "/stations/$stationId" });
  const requestId = search.recoveryRequestId ?? "";
  const { t } = useTranslation(["registrationRequests", "stations", "common"]);
  const station = useStationQuery(stationId);
  const registration = useRegistrationRequestQuery(requestId, requestId.length > 0);
  const decommission = useDecommissionStationMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!requestId) {
      return;
    }
    void Promise.all([station.refetch(), registration.refetch()]);
    // The canonical route identities are the only values that trigger a fresh recovery read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, stationId]);

  if (!requestId || station.isError || registration.isError) {
    return <ErrorState title={t("recoveryUnavailable")} detail={t("recoveryUnavailableDetail")} />;
  }
  if (station.isLoading || registration.isLoading || station.isFetching || registration.isFetching) {
    return <LoadingState />;
  }
  if (!station.data || !registration.data) {
    return <ErrorState title={t("recoveryUnavailable")} detail={t("recoveryUnavailableDetail")} />;
  }

  const value = station.data;
  const request = registration.data;
  const relation = request.enrollmentRelation;
  const relationValid = request.persistedStatus === "CONSUMED" && request.effectiveStatus === "CONSUMED" &&
    relation?.tokenKind === "REGISTRATION_V1" && relation.requestId === request.id && relation.stationId === value.id &&
    value.id === stationId && value.appId === request.appId && value.clientId === request.clientId &&
    value.installationId === request.installationId;
  if (!relationValid) {
    return <ErrorState title={t("integrityMismatch")} detail={t("integrityMismatchDetail")} onRetry={() => {
      void Promise.all([station.refetch(), registration.refetch()]);
    }} />;
  }

  const isDecommissioned = value.status === "DECOMMISSIONED" || Boolean(value.decommissionedAt);
  const submit = () => {
    const normalizedReason = reason.trim();
    if (!normalizedReason || isDecommissioned) {
      return;
    }
    decommission.mutate({ stationId: value.id, reason: normalizedReason }, {
      onSuccess: () => {
        setReason("");
        setDialogOpen(false);
        setCompleted(true);
      },
      onError: (error) => {
        if (error instanceof ApiError && error.code === "authentication_retry_required") {
          setDialogOpen(false);
          setReason("");
          void queryClient.invalidateQueries({ queryKey: ["station", value.id] });
          void queryClient.invalidateQueries({ queryKey: ["registration-request", request.id] });
          toast.error(t("authRetryRequired"));
          return;
        }
        toast.error(t("decommissionFailed"));
      }
    });
  };

  return (
    <div className="grid gap-4">
      <div>
        <h2 className="text-lg font-semibold">{t("recoveryStationTitle")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("recoveryStationDescription")}</p>
      </div>
      <section className="rounded-md border bg-panel p-4">
        <dl className="grid gap-3 text-sm md:grid-cols-2">
          <Meta label={t("stationId")} value={value.id} mono />
          <Meta label={t("appId")} value={value.appId} />
          <Meta label={t("clientId")} value={<ClientIdentity clientId={value.clientId} link={false} />} />
          <Meta label={t("installationId")} value={value.installationId} mono />
          <Meta label={t("stations:fingerprint")} value={value.fingerprintHash} mono />
          <Meta label={t("stations:status")} value={value.status} />
        </dl>
      </section>
      {completed || isDecommissioned ? (
        <section className="rounded-md border bg-panel p-4">
          <p className="font-medium">{t("recoveryComplete")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("freshRegistrationGuidance")}</p>
        </section>
      ) : (
        <section className="rounded-md border bg-panel p-4">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="destructive"><PowerOff className="h-4 w-4" />{t("stations:decommission")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("confirmExactStation")}</DialogTitle>
                <DialogDescription>{t("confirmExactStationDetail", { stationId: value.id })}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-2">
                <Label htmlFor="recovery-decommission-reason">{t("stations:reason")}</Label>
                <Textarea id="recovery-decommission-reason" value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common:actions.cancel")}</Button>
                <Button variant="destructive" onClick={submit} disabled={decommission.isPending || !reason.trim()}>
                  <PowerOff className="h-4 w-4" />{t("stations:decommissionSubmit")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </section>
      )}
    </div>
  );
}

function AdminControlCard({
  title,
  control,
  language,
  t,
  destructive,
  disabled,
  onEdit,
  onClear
}: {
  title: string;
  control: StationAdminControlRecord | undefined;
  language: string;
  t: (key: string) => string;
  destructive?: boolean;
  disabled: boolean;
  onEdit: () => void;
  onClear: () => void;
}) {
  const active = Boolean(control?.active);
  return (
    <div className="rounded-md border bg-panel p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium">{title}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Badge variant={active ? (destructive ? "destructive" : "success") : "default"}>
              {active ? t("active") : t("inactive")}
            </Badge>
            {active ? <span className="text-xs text-muted-foreground">{control?.validUntil ? formatDateTime(control.validUntil, language) : t("permanent")}</span> : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={disabled} onClick={onEdit}>
            <Pencil className="h-4 w-4" />
            {active ? t("edit") : t("set")}
          </Button>
          <Button size="sm" variant={destructive ? "destructive" : "outline"} disabled={disabled || !active} onClick={onClear}>
            <XCircle className="h-4 w-4" />
            {t("clear")}
          </Button>
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Meta label={t("validUntil")} value={active ? (control?.validUntil ? formatDateTime(control.validUntil, language) : t("permanent")) : "—"} />
        <Meta label={t("appliedAt")} value={formatDateTime(control?.appliedAt, language)} />
        <Meta label={t("appliedBy")} value={control?.appliedByEmployeeId || "—"} />
        <Meta label={t("reason")} value={control?.reason || "—"} />
      </div>
    </div>
  );
}

function ClearAdminControlDialog({
  open,
  title,
  description,
  reason,
  pending,
  destructive,
  onOpenChange,
  onReasonChange,
  onSubmit
}: {
  open: boolean;
  title: string;
  description: string;
  reason: string;
  pending: boolean;
  destructive?: boolean;
  onOpenChange: (open: boolean) => void;
  onReasonChange: (reason: string) => void;
  onSubmit: () => void;
}) {
  const { t } = useTranslation(["stations", "common"]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor={`clear-admin-control-${destructive ? "ban" : "allow"}`}>{t("reason")}</Label>
          <Textarea
            id={`clear-admin-control-${destructive ? "ban" : "allow"}`}
            value={reason}
            maxLength={500}
            onChange={(event) => onReasonChange(event.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common:actions.cancel")}
          </Button>
          <Button type="button" variant={destructive ? "destructive" : "default"} onClick={onSubmit} disabled={pending || reason.trim().length === 0}>
            <XCircle className="h-4 w-4" />
            {t("clear")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Header({ title }: { title: string }) {
  return <div className="border-b px-3 py-2 text-sm font-medium">{title}</div>;
}

function LicenseStateBlock({ state, language, t }: { state?: StationLicenseStateRecord; language: string; t: (key: string) => string }) {
  if (!state) {
    return <div className="p-4 text-sm text-muted-foreground">{t("common:states.loading")}</div>;
  }
  const Icon = state.effectiveAllowed ? ShieldCheck : ShieldX;
  const badgeVariant = state.effectiveAllowed ? "success" : "destructive";
  return (
    <div className="grid gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="h-5 w-5 text-muted-foreground" />
          <div className="min-w-0">
            <div className="text-sm font-medium">{state.effectiveAllowed ? t("effectiveAllowed") : t("effectiveBlocked")}</div>
            <div className="text-xs text-muted-foreground">{t(`reasonCode.${state.reason}`)}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant={badgeVariant}>{state.effectiveAllowed ? "ALLOWED" : "BLOCKED"}</Badge>
          {state.requiresRefresh ? <Badge variant="warning">{t("requiresRefresh")}</Badge> : null}
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Meta label={t("entitlement")} value={state.entitlementStatus || "—"} />
        <Meta label={t("checkedAt")} value={formatDateTime(state.entitlementCheckedAt, language)} />
        <Meta label={t("certNotAfter")} value={formatDateTime(state.cert?.notAfter, language)} />
        <Meta label={t("evaluatedAt")} value={formatDateTime(state.evaluatedAt, language)} />
        <Meta label={t("cert")} value={state.cert?.id || "—"} mono />
        <Meta label={t("adminBan")} value={formatAdminControl(state.adminBan, language, t)} />
        <Meta label={t("adminAllow")} value={formatAdminControl(state.adminAllow, language, t)} />
        <Meta label={t("override")} value={state.override?.id || "—"} mono />
        <Meta label={t("overrideValidUntil")} value={formatDateTime(state.override?.validUntil, language)} />
        <Meta label={t("certCarriesOverride")} value={state.certCarriesActiveOverride ? t("yes") : t("no")} />
      </div>
    </div>
  );
}

function Meta({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={mono ? "truncate font-mono text-xs" : "truncate text-sm"}>{value}</div>
    </div>
  );
}

function PolicyNumberInput({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (value: string) => void }) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={1}
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function dateTimeLocalToISO(value: string) {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
}

function dateTimeISOToLocal(value: string | undefined) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function defaultDateTimeLocal(hoursFromNow: number) {
  const date = new Date(Date.now() + hoursFromNow * 60 * 60 * 1000);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function formatAdminControl(control: StationAdminControlRecord | undefined, language: string, t: (key: string) => string) {
  if (!control?.active) {
    return t("inactive");
  }
  const until = control.validUntil ? formatDateTime(control.validUntil, language) : t("permanent");
  if (control.reason) {
    return `${t("active")} / ${until} / ${control.reason}`;
  }
  return `${t("active")} / ${until}`;
}

function parseOptionalPositiveInt(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

function formatPolicyNumber(value: number | undefined) {
  return value ? String(value) : "—";
}
